// InstancePoolRegistry
//
// Owns up to THREE GlobalInstancePool instances per fragment hash —
// one per LOD index (0=fine, 1=mid, 2=coarse). Per-tile LOD routing
// in StreamingController picks which pool a tile's instances live in;
// migration between LODs releases slots from the current pool and
// acquires fresh slots in the target pool. The same `onPoolCreated`
// callback fires for each pool so each per-LOD InstancedMesh joins
// `viewer.meshes` for raycast.
//
// Lazy creation: each (hash, lod) pool only materializes when actually
// needed. A model where every tile picks LOD 0 only allocates LOD-0
// pools. A tile that migrates to LOD 2 triggers lazy creation of the
// LOD-2 pool for that hash.
//
// === viewer.meshes integration ===
//
// `viewer.meshes` is the array Three.js / our raycaster scans for
// intersection tests. Each pool's InstancedMesh must appear there
// exactly once. Adding it twice (because two tiles claimed slots in
// the same pool) would produce duplicate raycast hits; never adding
// it would make the geometry unclickable.
//
// We solve this in the registry: when a pool is created (any LOD), the
// registry calls `onPoolCreated(pool)` which the streaming loader
// wires to `viewer.meshes.push(pool.mesh)`. The same callback also
// installs an `onMeshSwapped` hook on the pool so a grow swap
// updates the viewer.meshes entry in place.

import GlobalInstancePool from './GlobalInstancePool';

const LOD_COUNT = 3;

// First allocation of a pool (see getOrCreate): what its hash could need,
// within these bounds; growth past it doubles.
export const MIN_POOL_CAPACITY = 16;
export const MAX_INITIAL_POOL_CAPACITY = 256;

export default class InstancePoolRegistry {
  constructor({
    sceneRoot,
    tileRegistry = null,         // optional — used to pre-size new pools
    onPoolCreated = null,
    onMeshSwapped = null,
    onPoolDisposed = null,
  }) {
    this.onPoolDisposed = onPoolDisposed;
    if (!sceneRoot) throw new Error('InstancePoolRegistry: sceneRoot required');
    this.sceneRoot = sceneRoot;
    this.tileRegistry = tileRegistry;
    this.onPoolCreated = onPoolCreated;
    this.onMeshSwapped = onMeshSwapped;
    // hash -> [pool0, pool1, pool2] (sparse — only allocated LODs)
    this.pools = new Map();
  }

  // hasAny(hash) — at least one pool exists at any LOD for this hash.
  hasAny(hash) { return this.pools.has(hash); }

  // Back-compat: callers that pass just a hash get a truthy answer if
  // any pool exists for the hash. New per-LOD callers use hasAt(hash, lod).
  has(hash, lod = null) {
    if (lod == null) return this.hasAny(hash);
    const arr = this.pools.get(hash);
    return arr ? !!arr[lod] : false;
  }

  // Back-compat: single-arg get returns LOD-0 pool (the legacy default).
  // Per-LOD callers pass the explicit lod.
  get(hash, lod = 0) {
    const arr = this.pools.get(hash);
    return arr ? (arr[lod] || null) : null;
  }

  getOrCreate(hash, libraryParts, initialLod = 0) {
    let arr = this.pools.get(hash);
    if (!arr) {
      arr = new Array(LOD_COUNT).fill(null);
      this.pools.set(hash, arr);
    }
    if (arr[initialLod]) return arr[initialLod];

    // Pre-size from total instance count across every tile whose
    // sidecar has already loaded. Same calculation as before — the
    // total is an upper bound for any one LOD pool (since the pool
    // only sees the subset of tiles whose current LOD matches its own).
    // The 1.5× headroom absorbs late-arriving sidecars; per-LOD slack
    // is mostly wasted capacity. Acceptable for now — optimization
    // would require per-LOD-band tile counts which we don't track.
    //
    // CAPPED (2026-09-18). Every sidecar now loads up front, so "total"
    // became every instance of the hash in the MODEL, while the budget and
    // per-tile batching mean only a small part of it is ever drawn: on
    // SpaceX the pools held 1,063,659 slots with 4,017 in use - ~170 MB of
    // matrix / snapshot / colour / ghost arrays, all uploaded to the GPU at
    // full size on every flush. A pool now starts at what its hash could
    // need, between 16 and 256 slots, and doubles (_grow) when it fills.
    let initialCapacity = MIN_POOL_CAPACITY;
    if (this.tileRegistry) {
      let total = 0;
      this.tileRegistry.forEach(tile => {
        if (!tile.instancesByHash) return;
        const inst = tile.instancesByHash.get(hash);
        if (inst) total += inst.length;
      });
      if (total > 0) {
        initialCapacity = Math.min(MAX_INITIAL_POOL_CAPACITY, Math.max(MIN_POOL_CAPACITY, Math.ceil(total * 1.5)));
      }
    }

    const pool = new GlobalInstancePool({
      hash,
      libraryParts,
      initialLod,
      sceneRoot: this.sceneRoot,
      initialCapacity,
      onMeshSwapped: (oldMesh, newMesh) => {
        if (this.onMeshSwapped) this.onMeshSwapped(oldMesh, newMesh, pool);
      },
    });
    arr[initialLod] = pool;
    if (this.onPoolCreated) this.onPoolCreated(pool);
    return pool;
  }

  // Aggregate stats across every active pool — surfaced via streaming
  // .stats() so the user can see "how many draw calls am I doing". Each
  // entry includes its LOD so the readout reflects the 3-pool split.
  // Per-fragment LOD leaves pools behind: a tile that swapped from
  // medium to fine leaves the medium pool with every slot free, but its
  // instance buffers (capacity x 64 B matrices + colours) stay
  // allocated. Dispose pools that have had no live slot and no active
  // tile for `graceMs`, so a quick swap back does not thrash.
  disposeEmptyPools(now, graceMs = 5000) {
    let disposed = 0;
    this.pools.forEach((arr, hash) => {
      let any = false;
      for (let lod = 0; lod < arr.length; lod++) {
        const p = arr[lod];
        if (!p) continue;
        if (typeof p.trimCount === 'function') this.trimmedSlots = (this.trimmedSlots || 0) + p.trimCount();
        const live = (p.count || 0) - (p.freeSlots ? p.freeSlots.length : 0);
        const idle = live <= 0 && (!p.activeTiles || p.activeTiles.size === 0);
        if (!idle) { p._emptySince = 0; any = true; continue; }
        if (!p._emptySince) { p._emptySince = now; any = true; continue; }
        if (now - p._emptySince < graceMs) { any = true; continue; }
        const mesh = p.mesh;
        p.dispose();
        arr[lod] = null;
        disposed++;
        if (this.onPoolDisposed && mesh) this.onPoolDisposed(p, mesh);
      }
      if (!any) this.pools.delete(hash);
    });
    return disposed;
  }

  stats() {
    const pools = [];
    let totalCount = 0;
    let totalCapacity = 0;
    let totalFree = 0;
    let poolCount = 0;
    this.pools.forEach((arr, hash) => {
      for (let lod = 0; lod < arr.length; lod++) {
        const p = arr[lod];
        if (!p) continue;
        const s = p.stats();
        s.lod = lod;
        pools.push(s);
        totalCount += s.count;
        totalCapacity += s.capacity;
        totalFree += s.free;
        poolCount++;
      }
    });
    return {
      poolCount,                              // total live pools across all (hash, lod)
      hashCount:    this.pools.size,          // unique hashes (≤ poolCount)
      totalCount,
      totalCapacity,
      totalFree,
      pools,
    };
  }
}
